import {Component, EventEmitter, Input, Output, ViewChild} from '@angular/core';
import {DoButtonComponent} from '../do-button/do-button.component';
import {MessageFieldComponent} from '../do-button/message-field.component';

/**
 * The footer of every form and dialog with a primary action, one layout everywhere (styleguide
 * forms layout): secondary tools on the left (projected with the `footerStart` attribute), then
 * the status message, then the primary action far right.
 *
 *   <app-form-footer label="Start encode" icon="play" (action)="submit($event)">
 *     <button footerStart type="button" class="btn cl-color-tertiary">Load PO</button>
 *   </app-form-footer>
 *
 * A refused submit: `reportInvalid(button, form)` (submit-po/form-messages.ts) re-enables the button and
 * shows the count of fields needing attention until they are fixed.
 */
@Component({
  selector: 'app-form-footer',
  exportAs: 'formFooter',
  // Full width, also inside a dialog's flex .modal-footer: ms-auto needs the room to push the
  // primary action to the far right. Own styles, not utilities: castlabs-ui ships no global .w-100.
  styles: [`
    :host { display: block; width: 100%; flex: 1 1 auto; }
    .app-footer-action { gap: 1rem; }
  `],
  imports: [DoButtonComponent, MessageFieldComponent],
  template: `
    <div class="cl-spacing-box d-flex flex-wrap align-items-center" data-form-footer>
      <ng-content select="[footerStart]"></ng-content>
      <!-- Message + primary are one group pushed right: they always wrap together, the tools never split.
           Wide: one row. Narrower: tools, then message + primary on the right. Iframe/phone: stacked. -->
      <div class="app-footer-action d-flex flex-wrap align-items-center justify-content-end ms-auto">
        <app-message-field #message/>
        @if (showAction) {
          <app-do-button [buttonClass]="buttonClass" [disabled]="disabled" [messageField]="message"
                         (actionTriggered)="action.emit($event)">
            <span>{{ label }}</span>
            @if (icon) {
              <i [class]="'fas fa-' + icon" aria-hidden="true"></i>
            }
          </app-do-button>
        }
      </div>
    </div>`,
})
export class FormFooterComponent {
  /** The primary action's label: verb + noun ("Start encode", "Create workspace"). */
  @Input({required: true}) label!: string;
  /** Optional FontAwesome icon name after the label (e.g. 'play'). */
  @Input() icon: string | null = null;
  @Input() buttonClass = 'btn cl-color-primary';
  @Input() disabled = false;
  /** False hides the primary action (e.g. a dialog whose action is done and only Close remains). */
  @Input() showAction = true;
  @Output() action = new EventEmitter<DoButtonComponent>();

  /** The primary button, for forms that drive it directly (e.g. a reset that reports through it). */
  @ViewChild(DoButtonComponent) button!: DoButtonComponent;
  @ViewChild('message') message!: MessageFieldComponent;
}
